import { preserveSectionStyles } from './section-styles.js';

const fetchedHeaderSections = new Map();

function closeSiblingAccordions(summary) {
  const details = summary.closest('accordion-custom > details');
  const list = details?.closest('.menu-drawer__list-item')?.parentElement;

  if (!(details instanceof HTMLDetailsElement) || !(list instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    if (!details.open) return;

    list.querySelectorAll('accordion-custom > details[open]').forEach((sibling) => {
      if (sibling !== details && sibling instanceof HTMLDetailsElement) {
        sibling.open = false;
      }
    });
  });
}

function getHeaderSection(element) {
  return element.closest('.shopify-section[id^="shopify-section-"]');
}

function getHeaderSectionId(element) {
  return getHeaderSection(element)?.id?.replace(/^shopify-section-/, '') || '';
}

function getSectionRenderingURL(sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.set('section_id', sectionId);
  url.searchParams.sort();
  return url.toString();
}

async function fetchHeaderSection(element) {
  const sectionId = getHeaderSectionId(element);
  if (!sectionId) return null;

  const url = getSectionRenderingURL(sectionId);
  let sectionPromise = fetchedHeaderSections.get(url);

  if (!sectionPromise) {
    sectionPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Header section fetch failed: ${response.status}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch(() => null);
    fetchedHeaderSections.set(url, sectionPromise);
  }

  return sectionPromise;
}

function getTemplateFromContainer(container) {
  return container.querySelector(':scope > template[data-ch-lazy-drawer-template]');
}

async function getRemoteTemplate(container) {
  const key = container.dataset.chLazyDrawerKey;
  if (!key) return null;

  const documentFragment = await fetchHeaderSection(container);
  if (!documentFragment) return null;

  const remoteContainer = Array.from(documentFragment.querySelectorAll('[data-ch-lazy-drawer-content]')).find(
    (candidate) => candidate instanceof HTMLElement && candidate.dataset.chLazyDrawerKey === key
  );

  const template = remoteContainer ? getTemplateFromContainer(remoteContainer) : null;
  if (template) preserveSectionStyles(documentFragment, getHeaderSection(container) || container);
  return template;
}

async function hydrateLazyDrawerContent(root) {
  if (!(root instanceof Element)) return;

  const containers = Array.from(root.querySelectorAll('[data-ch-lazy-drawer-content]'));

  for (const container of containers) {
    if (!(container instanceof HTMLElement) || container.dataset.chLazyDrawerLoaded === 'true') continue;
    if (container.closest('details') !== root) continue;

    const template = getTemplateFromContainer(container) || (await getRemoteTemplate(container));
    if (!(template instanceof HTMLTemplateElement)) continue;

    container.replaceChildren(template.content.cloneNode(true));
    container.dataset.chLazyDrawerLoaded = 'true';
  }
}

function hydrateOpenedSummary(summary) {
  const details = summary.closest('details');
  if (!(details instanceof HTMLDetailsElement)) return;

  requestAnimationFrame(() => {
    hydrateLazyDrawerContent(details);

    details.querySelectorAll('accordion-custom > details[open]').forEach((openAccordion) => {
      hydrateLazyDrawerContent(openAccordion);
    });
  });
}

function handleAccordionClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const summary = target.closest('accordion-custom > details > summary');
  if (summary instanceof HTMLElement) {
    hydrateOpenedSummary(summary);
    closeSiblingAccordions(summary);
    return;
  }

  const drawerSummary = target.closest('header-drawer details > summary');
  if (drawerSummary instanceof HTMLElement) {
    hydrateOpenedSummary(drawerSummary);
  }
}

document.addEventListener('click', handleAccordionClick, true);
